import { memo, useCallback, useMemo } from 'react';
import ClanName from '@wg/wows-react-uikit/components/ClanName/ClanName';

import { isClanPreloaded } from '~/helpers/clan';
import {
  showClanInfoDialog,
  showClanLeaveDialog,
  showNameEditDialog,
  showRecommendationsClanSettingsDialog,
  showRulesDialog,
} from '~/helpers/dialogs';
import { useAppDispatch } from '~/store';

import ClanTitle from '~/UIKit/ClanTitle/ClanTitle';
import OwnClanTitle from '~/UIKit/OwnClanTitle/OwnClanTitle';

import styles from './ClanHeader.module.scss';
import ClanHeaderPreModerationHelp from './ClanHeaderPreModerationHelp';

import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { ClanPreloaded } from '~/types/declaration';

interface ClanHeaderTitleProps {
  clan: ClanInfoType | ClanPreloaded;
  currentAccount: ICurrentAccountState;
  isRenameInProcess: boolean;
  isOwnClanBase: boolean;
}

const ClanHeaderTitle = ({ clan, currentAccount, isRenameInProcess, isOwnClanBase }: ClanHeaderTitleProps) => {
  const dispatch = useAppDispatch();

  const isPreloaded = isClanPreloaded(clan);

  const hasClanDescription = useMemo(() => {
    return !!clan.rawDescription?.trim();
  }, [clan.rawDescription]);

  const handleClanInfoEditClick = useCallback(() => {
    dispatch(showClanInfoDialog({ clanId: clan.id, isInEditMode: true, isOpenedFromClanBase: true }));
  }, [dispatch, clan.id]);

  const handleRecommendationsClanSettingsClick = useCallback(() => {
    dispatch(showRecommendationsClanSettingsDialog());
  }, [dispatch]);

  const handleNameEditClick = useCallback(() => {
    dispatch(showNameEditDialog());
  }, [dispatch]);

  const handleLeaveClick = useCallback(() => {
    dispatch(showClanLeaveDialog());
  }, [dispatch]);

  const handleRulesDialogClick = useCallback(() => {
    dispatch(showRulesDialog());
  }, [dispatch]);

  if (isOwnClanBase) {
    return (
      <div className={styles.ownClanTitleWrap} key="clan-title-own">
        <ClanHeaderPreModerationHelp preModeration={clan.preModeration} />
        <OwnClanTitle
          needMenu
          clanTag={clan.tag}
          clanColor={clan.color}
          clanLevel={!isPreloaded ? clan.leveling : 0}
          accumulativeClanResource={!isPreloaded ? clan.accumulativeResource : 0}
          clanName={clan.name}
          hasClanDescription={hasClanDescription}
          isRenameInProcess={isRenameInProcess}
          roleName={currentAccount.roleName}
          onClanInfoEditClick={handleClanInfoEditClick}
          onRecommendationsClanSettingsClick={handleRecommendationsClanSettingsClick}
          onNameEditClick={handleNameEditClick}
          onLeaveClick={handleLeaveClick}
          onRulesDialogClick={handleRulesDialogClick}
        />
      </div>
    );
  }

  return (
    <div className={styles.clanTitleWrap} key="clan-title-external">
      <ClanTitle
        title={<ClanName clanTag={clan.tag} clanColor={clan.color} clanName={clan.name} />}
        isOwn={clan.id === currentAccount.clanId}
      />
    </div>
  );
};

export default memo(ClanHeaderTitle);
